import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Advertisement Management Component
 * Comprehensive CRUD operations for active advertisements
 */
const AdvertisementManagement = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [selectedAds, setSelectedAds] = useState([]);

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      const response = await axios.get(`${API}/admin/advertisements/all`);
      setAdvertisements(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setShowEditModal(true);
  };

  const handleDelete = async (adId) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      try {
        await axios.delete(`${API}/admin/advertisements/${adId}`);
        alert('Advertisement deleted successfully!');
        fetchAdvertisements();
      } catch (error) {
        alert('Failed to delete advertisement. Please try again.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAds.length === 0) {
      alert('Please select advertisements to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedAds.length} advertisements?`)) {
      try {
        await axios.delete(`${API}/admin/bulk-delete`, {
          data: { type: 'advertisements', ids: selectedAds }
        });
        alert(`${selectedAds.length} advertisements deleted successfully!`);
        setSelectedAds([]);
        fetchAdvertisements();
      } catch (error) {
        alert('Failed to delete advertisements. Please try again.');
      }
    }
  };

  const handleSelectAd = (adId) => {
    setSelectedAds(prev => 
      prev.includes(adId) 
        ? prev.filter(id => id !== adId)
        : [...prev, adId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAds.length === filteredAds.length) {
      setSelectedAds([]);
    } else {
      setSelectedAds(filteredAds.map(ad => ad.id));
    }
  };

  const handleCreate = () => {
    setShowCreateModal(true);
  };

  const filteredAds = advertisements.filter(ad => {
    const matchesSearch = (
      ad.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.advertiser_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesType = filterType === 'all' || 
                       (filterType === 'active' && ad.is_active) ||
                       (filterType === 'inactive' && !ad.is_active) ||
                       (filterType === 'banner' && ad.ad_type === 'banner') ||
                       (filterType === 'interstitial' && ad.ad_type === 'interstitial');
    
    return matchesSearch && matchesType;
  });

  const AdCard = ({ ad }) => (
    <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedAds.includes(ad.id)}
            onChange={() => handleSelectAd(ad.id)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300"
          />
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900">{ad.title}</h3>
            <p className="text-sm text-gray-600">ID: {ad.id}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(ad)}
            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50"
          >
            ✏️ Edit
          </button>
          <button
            onClick={() => handleDelete(ad.id)}
            className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-600 rounded hover:bg-red-50"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
      
      <div className="space-y-3 text-sm">
        <div>
          <p className="font-medium text-gray-800">Content:</p>
          <p className="text-gray-600">{ad.content}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-medium text-gray-800">Type:</p>
            <p className="text-gray-600 capitalize">{ad.ad_type}</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">Status:</p>
            <span className={`px-2 py-1 rounded-full text-xs ${
              ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {ad.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-medium text-gray-800">Budget:</p>
            <p className="text-gray-600">${parseFloat(ad.total_budget).toFixed(2)}</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">Spent:</p>
            <p className="text-gray-600">${parseFloat(ad.spent_budget || 0).toFixed(2)}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-medium text-gray-800">Views:</p>
            <p className="text-gray-600">{ad.views || 0}</p>
          </div>
          {/* <div>
            <p className="font-medium text-gray-800">Unique Views:</p>
            <p className="text-gray-600">{ad.unique_views || 0}</p>
          </div> */}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-medium text-gray-800">Clicks:</p>
            <p className="text-gray-600">{ad.clicks || 0}</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">CTR:</p>
            <p className="text-gray-600">
              {ad.views > 0 ? ((ad.clicks / ad.views) * 100).toFixed(2) : 0}%
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-medium text-gray-800">Start Date:</p>
            <p className="text-gray-600">{new Date(ad.start_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">End Date:</p>
            <p className="text-gray-600">{new Date(ad.end_date).toLocaleDateString()}</p>
          </div>
        </div>
        
        {ad.image_url && (
          <div>
            <p className="font-medium text-gray-800">Image:</p>
            <img src={ad.image_url} alt="Ad" className="mt-2 max-w-full h-32 object-cover rounded" />
          </div>
        )}
        
        {ad.click_url && (
          <div>
            <p className="font-medium text-gray-800">Click URL:</p>
            <a href={ad.click_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs break-all">
              {ad.click_url}
            </a>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-4xl mb-4">📢</div>
          <p className="text-gray-600">Loading advertisements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Advertisement Management</h2>
          <p className="text-gray-600 mt-1">Manage active advertisements and campaigns</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCreate}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            ➕ Create Advertisement
          </button>
          {selectedAds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              🗑️ Delete Selected ({selectedAds.length})
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="text-blue-600 text-3xl mr-4">📢</div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Ads</p>
              <p className="text-2xl font-bold text-blue-800">{advertisements.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center">
            <div className="text-green-600 text-3xl mr-4">✅</div>
            <div>
              <p className="text-sm text-green-600 font-medium">Active</p>
              <p className="text-2xl font-bold text-green-800">
                {advertisements.filter(ad => ad.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
          <div className="flex items-center">
            <div className="text-yellow-600 text-3xl mr-4">🖼️</div>
            <div>
              <p className="text-sm text-yellow-600 font-medium">Banner Ads</p>
              <p className="text-2xl font-bold text-yellow-800">
                {advertisements.filter(ad => ad.ad_type === 'banner').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="text-purple-600 text-3xl mr-4">🔲</div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Interstitial</p>
              <p className="text-2xl font-bold text-purple-800">
                {advertisements.filter(ad => ad.ad_type === 'interstitial').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search advertisements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex space-x-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Ads</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="banner">Banner Ads</option>
              <option value="interstitial">Interstitial Ads</option>
            </select>
            
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {selectedAds.length === filteredAds.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <button
              onClick={fetchAdvertisements}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Advertisements List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Advertisements ({filteredAds.length})
        </h3>
        
        {filteredAds.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-gray-500">
              {searchTerm || filterType !== 'all' 
                ? 'No advertisements match your search criteria' 
                : 'No advertisements found'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAds.map(ad => (
              <AdCard key={ad.id} ad={ad} />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <AdEditModal
          ad={editingAd}
          onClose={() => {
            setShowEditModal(false);
            setEditingAd(null);
          }}
          onUpdate={fetchAdvertisements}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <AdCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={fetchAdvertisements}
        />
      )}
    </div>
  );
};

/**
 * Advertisement Edit Modal Component
 */
const AdEditModal = ({ ad, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: ad.title || '',
    content: ad.content || '',
    image_url: ad.image_url || '',
    click_url: ad.click_url || '',
    ad_type: ad.ad_type || 'banner',
    target_audience: ad.target_audience || '',
    total_budget: ad.total_budget || 0,
    spent_budget: ad.spent_budget || 0,
    start_date: ad.start_date?.substring(0, 10) || '',
    end_date: ad.end_date?.substring(0, 10) || '',
    is_active: ad.is_active || false,
    cost_per_view: ad.cost_per_view || 0.01,
    views: ad.views || 0,
    unique_views: ad.unique_views || 0,
    clicks: ad.clicks || 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/admin/advertisements/${ad.id}`, formData);
      alert('Advertisement updated successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      alert('Failed to update advertisement. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Advertisement</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Click URL</label>
              <input
                type="url"
                value={formData.click_url}
                onChange={(e) => handleChange('click_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ad Type</label>
              <select
                value={formData.ad_type}
                onChange={(e) => handleChange('ad_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="banner">Banner</option>
                <option value="interstitial">Interstitial</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
              <input
                type="text"
                value={formData.target_audience}
                onChange={(e) => handleChange('target_audience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Budget</label>
              <input
                type="number"
                value={formData.total_budget}
                onChange={(e) => handleChange('total_budget', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Spent Budget</label>
              <input
                type="number"
                value={formData.spent_budget}
                onChange={(e) => handleChange('spent_budget', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost per View</label>
              <input
                type="number"
                value={formData.cost_per_view}
                onChange={(e) => handleChange('cost_per_view', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clicks</label>
              <input
                type="number"
                value={formData.clicks}
                onChange={(e) => handleChange('clicks', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Views</label>
              <input
                type="number"
                value={formData.views}
                onChange={(e) => handleChange('views', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unique User Views</label>
              <input
                type="number"
                value={formData.unique_views}
                onChange={(e) => handleChange('unique_views', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active advertisement
            </label>
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update Advertisement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Advertisement Create Modal Component
 */
const AdCreateModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    click_url: '',
    ad_type: 'banner',
    target_audience: 'all',
    total_budget: 100,
    cost_per_view: 0.01,
    start_date: new Date().toISOString().substring(0, 10),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10),
    is_active: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/advertisements`, formData);
      alert('Advertisement created successfully!');
      onCreate();
      onClose();
    } catch (error) {
      alert('Failed to create advertisement. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create New Advertisement</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Click URL</label>
              <input
                type="url"
                value={formData.click_url}
                onChange={(e) => handleChange('click_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ad Type *</label>
              <select
                value={formData.ad_type}
                onChange={(e) => handleChange('ad_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="banner">Banner</option>
                <option value="interstitial">Interstitial</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience *</label>
              <select
                value={formData.target_audience}
                onChange={(e) => handleChange('target_audience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Users</option>
                <option value="bus_users">Bus Users</option>
                <option value="private_users">Private Users</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Budget *</label>
              <input
                type="number"
                value={formData.total_budget}
                onChange={(e) => handleChange('total_budget', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost per View</label>
              <input
                type="number"
                value={formData.cost_per_view}
                onChange={(e) => handleChange('cost_per_view', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.001"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active advertisement
            </label>
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Create Advertisement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvertisementManagement;